
import React, { useState, useEffect } from 'react';
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Search, UserPlus, UserCheck, UserX, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Database } from '@/integrations/supabase/types';
import { useNavigate } from 'react-router-dom';

type Profile = Database['public']['Tables']['profiles']['Row'];
type Follower = Database['public']['Tables']['followers']['Row'];

// Create a simplified profile type for the UI that only contains
// the properties we actually display and use
interface SimpleUserProfile {
  id: string;
  full_name: string | null;
  username: string | null;
  avatar_url: string | null;
  isFollowing?: boolean;
}

interface FriendsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  initialTab?: 'search' | 'followers' | 'following';
  viewUserId?: string; // ID of the user whose profile we're viewing
}

export default function FriendsSheet({ 
  isOpen, 
  onOpenChange, 
  initialTab = 'search',
  viewUserId
}: FriendsSheetProps) {
  const [activeTab, setActiveTab] = useState<'search' | 'followers' | 'following'>(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SimpleUserProfile[]>([]);
  const [followers, setFollowers] = useState<SimpleUserProfile[]>([]);
  const [following, setFollowing] = useState<SimpleUserProfile[]>([]);
  const [loading, setLoading] = useState<{[key: string]: boolean}>({
    search: false,
    followers: false,
    following: false
  });
  const [followLoading, setFollowLoading] = useState<{[key: string]: boolean}>({});
  
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Set active tab when initialTab prop changes
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Function to get initials for avatar fallback
  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };
  
  // Search for users with proper debouncing
  const searchUsers = async (query: string) => {
    if (!query.trim() || !user) {
      setSearchResults([]);
      return;
    }
    
    setLoading(prev => ({ ...prev, search: true }));
    try {
      console.log('Searching for users with query:', query);
      
      const { data: userProfiles, error } = await supabase
        .from('profiles')
        .select('id, full_name, username, avatar_url')
        .or(`full_name.ilike.%${query}%,username.ilike.%${query}%`)
        .neq('id', user.id) // Don't show the current user
        .limit(20);
      
      if (error) throw error;
      
      // Get following status for each user
      const { data: followingData } = await supabase
        .from('followers')
        .select('following_id')
        .eq('follower_id', user.id);
      
      const followingIds = followingData?.map(follow => follow.following_id) || [];
      
      const results = (userProfiles || []).map(profile => ({
        id: profile.id,
        full_name: profile.full_name,
        username: profile.username,
        avatar_url: profile.avatar_url,
        isFollowing: followingIds.includes(profile.id)
      }));
      
      console.log('Search results:', results);
      setSearchResults(results);
    } catch (error: any) {
      console.error('Error searching users:', error);
      toast.error('Error searching users: ' + error.message);
      setSearchResults([]);
    } finally {
      setLoading(prev => ({ ...prev, search: false }));
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUsers(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, user]);

  // Clear search when tab changes or sheet closes
  useEffect(() => {
    if (!isOpen || activeTab !== 'search') {
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [isOpen, activeTab]);
  
  // Fetch followers
  const fetchFollowers = async () => {
    if ((!user && !viewUserId) || loading.followers) return;
    
    setLoading(prev => ({ ...prev, followers: true }));
    try {
      // Get users who follow the current user or viewed user
      const targetUserId = viewUserId || user?.id;
      
      const { data, error } = await supabase
        .from('followers')
        .select(`
          follower_id,
          profiles!followers_follower_id_fkey(id, full_name, username, avatar_url)
        `)
        .eq('following_id', targetUserId);
      
      if (error) throw error;
      
      // Also get the users that the current user follows (to show follow status)
      let followingIds: string[] = [];
      
      if (user) {
        const { data: followingData } = await supabase
          .from('followers')
          .select('following_id')
          .eq('follower_id', user.id);
        
        followingIds = followingData?.map(follow => follow.following_id) || [];
      }
      
      const followersList = (data || []).map((item: any) => ({
        id: item.profiles.id,
        full_name: item.profiles.full_name,
        username: item.profiles.username,
        avatar_url: item.profiles.avatar_url,
        isFollowing: followingIds.includes(item.profiles.id)
      }));
      
      setFollowers(followersList);
    } catch (error: any) {
      toast.error('Error fetching followers: ' + error.message);
    } finally {
      setLoading(prev => ({ ...prev, followers: false }));
    }
  };
  
  // Fetch following
  const fetchFollowing = async () => {
    if ((!user && !viewUserId) || loading.following) return;
    
    setLoading(prev => ({ ...prev, following: true }));
    try {
      // Get users that the current user or viewed user follows
      const targetUserId = viewUserId || user?.id;
      
      const { data, error } = await supabase
        .from('followers')
        .select(`
          following_id,
          profiles!followers_following_id_fkey(id, full_name, username, avatar_url)
        `)
        .eq('follower_id', targetUserId);
      
      if (error) throw error;
      
      // For the current user, all these users are followed
      // For a viewed user, we need to check which ones the current user follows
      let followingIds: string[] = [];
      
      if (user && viewUserId) {
        const { data: currentFollowingData } = await supabase
          .from('followers')
          .select('following_id')
          .eq('follower_id', user.id);
        
        followingIds = currentFollowingData?.map(follow => follow.following_id) || [];
      }
      
      const followingList = (data || []).map((item: any) => ({
        id: item.profiles.id,
        full_name: item.profiles.full_name,
        username: item.profiles.username,
        avatar_url: item.profiles.avatar_url,
        isFollowing: viewUserId ? followingIds.includes(item.profiles.id) : true
      }));
      
      setFollowing(followingList);
    } catch (error: any) {
      toast.error('Error fetching following: ' + error.message);
    } finally {
      setLoading(prev => ({ ...prev, following: false }));
    }
  };
  
  // Follow/unfollow a user
  const toggleFollow = async (profileId: string, isCurrentlyFollowing: boolean) => {
    if (!user) return;
    
    setFollowLoading(prev => ({ ...prev, [profileId]: true }));
    try {
      if (isCurrentlyFollowing) {
        // Unfollow
        const { error } = await supabase
          .from('followers')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', profileId);
        
        if (error) throw error;
        toast.success('Unfollowed successfully');
      } else {
        // Follow
        const { error } = await supabase
          .from('followers')
          .insert({
            follower_id: user.id,
            following_id: profileId
          });
        
        if (error) throw error;
        toast.success('Followed successfully');
      }
      
      // Update UI based on active tab
      if (activeTab === 'search') {
        setSearchResults(prev => 
          prev.map(profile => 
            profile.id === profileId 
              ? { ...profile, isFollowing: !isCurrentlyFollowing } 
              : profile
          )
        );
      } else if (activeTab === 'following') {
        if (isCurrentlyFollowing) {
          setFollowing(prev => prev.filter(profile => profile.id !== profileId));
        }
      } else if (activeTab === 'followers') {
        // Update follower status if we're on the followers tab
        setFollowers(prev => 
          prev.map(profile => 
            profile.id === profileId 
              ? { ...profile, isFollowing: !isCurrentlyFollowing } 
              : profile
          )
        );
      }
      
    } catch (error: any) {
      toast.error(`Error ${isCurrentlyFollowing ? 'unfollowing' : 'following'} user: ${error.message}`);
    } finally {
      setFollowLoading(prev => ({ ...prev, [profileId]: false }));
    }
  };
  
  // Navigate to user profile
  const navigateToProfile = (profileId: string) => {
    onOpenChange(false); // Close the sheet
    navigate(`/profile/${profileId}`);
  };
  
  // Load appropriate data when tab changes or when sheet opens
  useEffect(() => {
    if (!isOpen) return;
    
    if (activeTab === 'followers') {
      fetchFollowers();
    } else if (activeTab === 'following') {
      fetchFollowing();
    }
  }, [activeTab, isOpen, user, viewUserId]);
  
  // Render user card with responsive design
  const UserCard = ({ profile }: { profile: SimpleUserProfile }) => (
    <div className="flex items-center justify-between p-2 sm:p-3 border-b last:border-b-0">
      <div 
        className="flex items-center gap-2 sm:gap-3 cursor-pointer flex-1 min-w-0" 
        onClick={() => navigateToProfile(profile.id)}
      >
        <Avatar className="h-8 w-8 sm:h-10 sm:w-10 cursor-pointer flex-shrink-0">
          <AvatarImage src={profile.avatar_url || ''} />
          <AvatarFallback>{getInitials(profile.full_name || profile.username)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm sm:text-base truncate">{profile.full_name || 'No name'}</p>
          <p className="text-xs sm:text-sm text-muted-foreground truncate">@{profile.username || 'no-username'}</p>
        </div>
      </div>
      
      {profile.id !== user?.id && user && (
        <Button 
          variant={profile.isFollowing ? "outline" : "default"}
          size="sm"
          className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2 ml-2 flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation(); // Prevent navigation when clicking button
            toggleFollow(profile.id, !!profile.isFollowing);
          }}
          disabled={!!followLoading[profile.id]}
        >
          {followLoading[profile.id] ? (
            <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
          ) : profile.isFollowing ? (
            <>
              <UserCheck className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Following</span>
              <span className="sm:hidden">✓</span>
            </>
          ) : (
            <>
              <UserPlus className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Follow</span>
              <span className="sm:hidden">+</span>
            </>
          )}
        </Button>
      )}
    </div>
  );
  
  const title = viewUserId ? (activeTab === 'followers' ? 'Followers' : 'Following') : 'Friends';
  
  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md p-4 sm:p-6">
        <SheetHeader className="mb-4 sm:mb-6">
          <SheetTitle className="text-lg sm:text-xl">{title}</SheetTitle>
        </SheetHeader>
        
        <Tabs 
          value={activeTab} 
          onValueChange={(value: 'search' | 'followers' | 'following') => setActiveTab(value)}
          className="h-full flex flex-col"
        >
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="search" disabled={!!viewUserId} className="text-xs sm:text-sm">
              Search
            </TabsTrigger>
            <TabsTrigger value="followers" className="text-xs sm:text-sm">
              Followers
            </TabsTrigger>
            <TabsTrigger value="following" className="text-xs sm:text-sm">
              Following
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="search" className="flex-1 flex flex-col">
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
                <Input 
                  placeholder="Search by name or username..." 
                  className="pl-10 text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {loading.search ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-padel-green-light" />
                </div>
              ) : searchResults.length > 0 ? (
                searchResults.map(profile => (
                  <UserCard key={profile.id} profile={profile} />
                ))
              ) : searchQuery.trim() ? (
                <p className="text-center py-8 text-muted-foreground text-sm">No users found</p>
              ) : (
                <p className="text-center py-8 text-muted-foreground text-sm">Search for players by name or username</p>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="followers" className="flex-1 flex flex-col">
            <div className="flex-1 overflow-y-auto">
              {loading.followers ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-padel-green-light" />
                </div>
              ) : followers.length > 0 ? (
                followers.map(profile => (
                  <UserCard key={profile.id} profile={profile} />
                ))
              ) : (
                <p className="text-center py-8 text-muted-foreground text-sm">
                  {viewUserId ? "This user doesn't have any followers yet" : "You don't have any followers yet"}
                </p>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="following" className="flex-1 flex flex-col">
            <div className="flex-1 overflow-y-auto">
              {loading.following ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-padel-green-light" />
                </div>
              ) : following.length > 0 ? (
                following.map(profile => (
                  <UserCard key={profile.id} profile={profile} />
                ))
              ) : (
                <p className="text-center py-8 text-muted-foreground text-sm">
                  {viewUserId ? "This user isn't following anyone yet" : "You aren't following anyone yet"}
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
